/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `common` for the Caintra Connect frontend.
 * @module utils/common
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { toast } from "react-toastify";
import { logger, sanitizeError } from "./logger.js";
import {
  apiMessageOrGeneric,
  getGenericErrorMessage,
} from "./genericErrorMessages.js";
import { Buffer } from "buffer";
import {
  extractAssetsObjectKey as extractAssetsObjectKeyFromMiddleware,
  fetchAssetObjectUrl as fetchAssetObjectUrlFromMiddleware,
  generateAssetUrl as generateAssetUrlFromMiddleware,
  generateImageUrl as generateImageUrlFromMiddleware,
  resolveAccessibleAssetUrl as resolveAccessibleAssetUrlFromMiddleware,
} from "./assetUrl";
import {
  stripMobileCountryPrefix,
  validateMobilePhoneNumber,
} from "./mobilePhone.js";

export {
  CERTIFICATION_OPTION_REQUIREMENT_PREFIX,
  getProfileByNumber,
  getProfileFieldValue,
  PROFILE_EDITABLE_FIELDS,
  isProfileEditableField,
  getCompanyFieldValue,
  parseCertificationOptionRequirementKey,
  getRequirementFieldValue,
  PROFILE_HIDDEN_FIELDS,
  shouldHideFieldForProfile,
  PROFILE_READONLY_FIELDS,
  isFieldReadOnlyForProfile,
} from "./profileFieldUtils.js";

export {
  parseExcelFile,
  handleDownloadBuyerCompaniesExcelLayout,
  getRequirementsExcelFieldDefinitions,
  handleDownloadRequirementsExcelLayout,
} from "./excelTemplateUtils.js";

// Constants to avoid string literal duplication
const NOT_REQUIRED = "No requerido";
const RFC_PHYSICAL_DATE_START = 4;
const RFC_MORAL_DATE_START = 3;
const RFC_DATE_LENGTH = 6;
const DATE_MONTH_START = 2;
const DATE_DAY_START = 4;
const DATE_COMPONENT_END = 6;
const MIN_MONTH = 1;
const MAX_MONTH = 12;
const MIN_DAY = 1;
const MAX_DAY = 31;
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;
const NATIONAL_PHONE_PATTERN = new RegExp(
  `^[1-9]\\d{${PHONE_MIN_DIGITS - 1},${PHONE_MAX_DIGITS - 1}}$`,
);
const SELECT_BORDER_RADIUS = "8px";
const SELECT_FONT_SIZE = "14px";
const SELECT_MARGIN_BOTTOM = "-10px";
const SELECT_ERROR_BORDER_COLOR = "#dc3545";

/** RFCs that are considered CAINTRA (e.g. for role-dropdown filtering in user logic). */
export const CAINTRA_RFCS = ["CIT4402264P8"];

/**
 * Refreshes the auth token using the stored refresh token.
 * @returns {Promise<{token: string}|null>} New access token or null on failure
 */
export const refreshToken = async () => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      refreshToken: localStorage.getItem("refreshToken"),
    }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}auth/token/refresh`,
      options,
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(apiMessageOrGeneric(data?.message));
    }
    localStorage.setItem("token", data.AccessToken);
    return {
      token: data.AccessToken,
    };
  } catch (error) {
    logger.error("RefreshToken Error: ", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Returns the auth token from localStorage, or null if missing/invalid.
 * @returns {string|null}
 */
export const getToken = () => {
  const token = localStorage.getItem("token");
  if (token && token !== "undefined" && token !== "null") {
    return token;
  }
  return null;
};

const MISSING_TOKEN_LOG =
  "Error trying to get the token to retrieve the users data. Token value:";

/**
 * Reads the session token and logs when it is missing.
 * Keeps the modal check that treats the literal "null" as present.
 * @returns {string|null}
 */
export const getLoggedAuthToken = () => {
  const token = localStorage.getItem("token");
  if (token && token !== "undefined") {
    return token;
  }
  logger.error(MISSING_TOKEN_LOG, { data: token });
  return null;
};

/**
 * Signs out the user: calls DELETE /auth/session and clears localStorage.
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  const userId = localStorage.getItem("userId");
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const options = {
    method: "DELETE",
    headers,
    body: JSON.stringify({ userId }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}auth/session`,
      options,
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(apiMessageOrGeneric(data?.message));
    }
    window.dataLayer.push({
      event: "logout",
      user_id: userId,
    });
  } catch (error) {
    logger.error("Error: ", { err: sanitizeError(error) });
  } finally {
    localStorage.clear();
  }
};

/** Hex radix used when percent-encoding one JWT payload byte. */
const JWT_BYTE_HEX_RADIX = 16;

/** Character width of one percent-encoded byte (`%HH`). */
const JWT_PERCENT_ENCODED_BYTE_WIDTH = 2;

/**
 * Decodes the payload of a JWT without verifying the signature.
 * @param {string} token
 * @returns {Object}
 */
export const parseJwt = (token) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const base = Buffer.from(base64, "base64").toString("binary");
  const jsonPayload = decodeURIComponent(
    base
      .split("")
      .map(function (c) {
        const hex = c.charCodeAt(0).toString(JWT_BYTE_HEX_RADIX);
        return (
          "%" +
          ("0".repeat(JWT_PERCENT_ENCODED_BYTE_WIDTH) + hex).slice(
            -JWT_PERCENT_ENCODED_BYTE_WIDTH,
          )
        );
      })
      .join(""),
  );
  return JSON.parse(jsonPayload);
};

/**
 * Returns the S3 object key stored in an asset URL or proxy ref.
 * @type {Function}
 */
export const extractAssetsObjectKey = extractAssetsObjectKeyFromMiddleware;

/**
 * Resolves a media or document reference to a URL the browser can GET.
 * Sensitive prefixes are exchanged for a short-lived presigned S3 URL; product media uses /assets/{ref}.
 * @param {string} src
 * @returns {Promise<string>}
 */
export const resolveAccessibleAssetUrl = async (src = "") =>
  resolveAccessibleAssetUrlFromMiddleware(src, { getToken, refreshToken });

/**
 * Builds the display URL for a public asset key.
 * @type {Function}
 */
export const generateAssetUrl = generateAssetUrlFromMiddleware;

/**
 * Builds the display URL for an image asset, adding a cache buster when needed.
 * @type {Function}
 */
export const generateImageUrl = generateImageUrlFromMiddleware;

/**
 * Fetches an asset and returns a blob URL the browser can render.
 * @type {Function}
 */
export const fetchAssetObjectUrl = fetchAssetObjectUrlFromMiddleware;

/**
 * Finds a select option whose numeric value matches the stored value.
 * @param {Array<{value: number}>|undefined} options
 * @param {number|string|null|undefined} value
 * @returns {Object|null}
 */
export const getSelectedOption = (options, value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return options?.find((option) => option.value === Number(value)) || null;
};

/**
 * Same as getSelectedOption but when value is 0 and no option matches (e.g. options
 * are 1-based), returns the first option so that 0 displays correctly.
 *
 * @param {Array<{value: number, label: string}>} options - Select options
 * @param {number|string|null|undefined} value - Stored value (e.g. 0, "0", 1, 2)
 * @returns {Object|null} Matched option, first option when value is 0 and no match, or null
 */
export const getSelectedOptionWithZeroFirst = (options, value) => {
  if (value === null || value === undefined || String(value) === "") {
    return null;
  }
  const option = options?.find((opt) => opt.value === Number(value)) || null;
  if (option) {
    return option;
  }
  if (Number(value) === 0 && options?.length > 0) {
    return options[0];
  }
  return null;
};

/**
 * Finds a country-code option by its exact value.
 * @param {Array<{value: string}>} options
 * @param {string} value
 * @returns {Object|null}
 */
export const getSelectedCountryCodeOption = (options, value) => {
  return options.find((option) => option.value === value) || null;
};

/** CountryCodeSelector value for Mexico (+52). */
export const DEFAULT_MEXICO_COUNTRY_CODE = "MX";

export { stripMobileCountryPrefix, validateMobilePhoneNumber };

/**
 * Default invoice phone fields from the user profile (`numeroCelular`).
 * Country code is always Mexico (+52 / MX).
 * @param {Object|null|undefined} user
 * @returns {{telefonoCodigoPais: string, telefono: string}}
 */
export const getInvoicePhoneDefaultsFromUser = (user) => ({
  telefonoCodigoPais: DEFAULT_MEXICO_COUNTRY_CODE,
  telefono: user?.numeroCelular?.trim()
    ? stripMobileCountryPrefix(user.numeroCelular)
    : "",
});

/**
 * Compares reference-data ids. A company may store numbers while the API uses string `_id`.
 * @param {string|number|null|undefined} a
 * @param {string|number|null|undefined} b
 * @returns {boolean}
 */
export const areReferenceIdsEqual = (a, b) => {
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }
  return String(a) === String(b);
};

/**
 * Extracts an id from a scalar or `{ _id }` reference, such as a company payload.
 * @param {string|number|Object|null|undefined} value
 * @returns {string|number|null|undefined}
 */
export const normalizeReferenceId = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "object" && value !== null && "_id" in value) {
    return value._id;
  }
  return value;
};

/**
 * Finds a reference-data option by `_id`.
 * @param {Array<{_id: string}>} options
 * @param {string|number|Object|null|undefined} id
 * @returns {Object|null}
 */
export const getReferencedDataOptionById = (options, id) => {
  const normalizedId = normalizeReferenceId(id);
  if (normalizedId === null || normalizedId === undefined) {
    return null;
  }
  return (
    options.find((option) => areReferenceIdsEqual(option._id, normalizedId)) ||
    null
  );
};

/**
 * Finds a top-level SCIAN sector by `_id` in the sectors API tree.
 * @param {Array<Object>|undefined} sectorsList
 * @param {string|number|null|undefined} sectorId
 * @returns {Object|null}
 */
export const findSectorByIdInTree = (sectorsList, sectorId) => {
  if (
    !Array.isArray(sectorsList) ||
    sectorId === null ||
    sectorId === undefined
  ) {
    return null;
  }
  return (
    sectorsList.find((sector) => areReferenceIdsEqual(sector._id, sectorId)) ??
    null
  );
};

/**
 * Finds a SCIAN subsector by `_id` anywhere in the sectors API tree.
 * @param {Array<Object>|undefined} sectorsList
 * @param {string|number|Object|null|undefined} subSectorId
 * @returns {Object|null}
 */
export const findSubSectorInSectorTree = (sectorsList, subSectorId) => {
  const normalizedSubSectorId = normalizeReferenceId(subSectorId);
  if (
    !Array.isArray(sectorsList) ||
    normalizedSubSectorId === null ||
    normalizedSubSectorId === undefined
  ) {
    return null;
  }

  for (const sector of sectorsList) {
    const subSector = (sector.subSectores ?? []).find((ss) =>
      areReferenceIdsEqual(ss._id, normalizedSubSectorId),
    );
    if (subSector) {
      return subSector;
    }
  }

  return null;
};

/**
 * Finds a SCIAN rama (branch) by `_id` anywhere in the sectors API tree.
 * @param {Array<Object>|undefined} sectorsList
 * @param {string|number|Object|null|undefined} ramaId
 * @returns {Object|null}
 */
export const findRamaInSectorTree = (sectorsList, ramaId) => {
  const normalizedRamaId = normalizeReferenceId(ramaId);
  if (
    !Array.isArray(sectorsList) ||
    normalizedRamaId === null ||
    normalizedRamaId === undefined
  ) {
    return null;
  }

  for (const sector of sectorsList) {
    for (const subSector of sector.subSectores ?? []) {
      const rama = (subSector.ramas ?? []).find((branch) =>
        areReferenceIdsEqual(branch._id, normalizedRamaId),
      );
      if (rama) {
        return rama;
      }
    }
  }

  return null;
};

/**
 * Resolves sector, subsector, and branch options from the nested sectors API tree.
 * A company's sector and subsector may belong to different branches, so an id that is
 * missing under its parent is looked up across the whole tree and appended to the lists.
 * @param {Array<Object>} sectorsList
 * @param {string|number|null|undefined} sectorId
 * @param {string|number|Object|null|undefined} subSectorId
 * @param {string|number|Object|null|undefined} ramaId
 * @returns {{sector: Object|null, subSectors: Array, subSector: Object|null, branches: Array, rama: Object|null}}
 */
export const resolveSectorClassificationFromTree = (
  sectorsList,
  sectorId,
  subSectorId,
  ramaId,
) => {
  const empty = {
    sector: null,
    subSectors: [],
    subSector: null,
    branches: [],
    rama: null,
  };
  if (!Array.isArray(sectorsList) || sectorsList.length === 0) {
    return empty;
  }

  const sector = findSectorByIdInTree(sectorsList, sectorId);

  const subSectors = [...(sector?.subSectores ?? [])];
  const normalizedSubSectorId = normalizeReferenceId(subSectorId);
  let subSector = getReferencedDataOptionById(
    subSectors,
    normalizedSubSectorId,
  );
  if (
    !subSector &&
    normalizedSubSectorId !== null &&
    normalizedSubSectorId !== undefined
  ) {
    subSector = findSubSectorInSectorTree(sectorsList, normalizedSubSectorId);
    if (subSector) {
      subSectors.push(subSector);
    }
  }

  const branches = [...(subSector?.ramas ?? [])];
  const normalizedRamaId = normalizeReferenceId(ramaId);
  let rama = getReferencedDataOptionById(branches, normalizedRamaId);
  if (
    !rama &&
    normalizedRamaId !== null &&
    normalizedRamaId !== undefined
  ) {
    rama = findRamaInSectorTree(sectorsList, normalizedRamaId);
    if (rama) {
      branches.push(rama);
    }
  }

  return { sector, subSectors, subSector, branches, rama };
};

/**
 * Resolves a SCIAN sector id to its `nombre`. Returns the input when it is already a label.
 * @param {Array<Object>|undefined} sectorsList
 * @param {string|number|null|undefined} sectorIdOrLabel
 * @returns {string|number|null|undefined}
 */
export const resolveSectorLabelFromTree = (sectorsList, sectorIdOrLabel) => {
  if (
    sectorIdOrLabel === null ||
    sectorIdOrLabel === undefined ||
    sectorIdOrLabel === ""
  ) {
    return sectorIdOrLabel;
  }
  return (
    findSectorByIdInTree(sectorsList, sectorIdOrLabel)?.nombre ??
    sectorIdOrLabel
  );
};

/**
 * Resolves a SCIAN rama id to its `nombre`. Returns the input when it is already a label.
 * @param {Array<Object>|undefined} sectorsList
 * @param {string|number|null|undefined} ramaIdOrLabel
 * @returns {string|number|null|undefined}
 */
export const resolveRamaLabelFromTree = (sectorsList, ramaIdOrLabel) => {
  if (
    ramaIdOrLabel === null ||
    ramaIdOrLabel === undefined ||
    ramaIdOrLabel === ""
  ) {
    return ramaIdOrLabel;
  }
  return (
    findRamaInSectorTree(sectorsList, ramaIdOrLabel)?.nombre ?? ramaIdOrLabel
  );
};

/**
 * Finds a reference-data option by its `nombre`.
 * @param {Array<{nombre: string}>} options
 * @param {string} name
 * @returns {Object|null}
 */
export const getReferencedDataOptionByName = (options, name) => {
  return options.find((option) => option.nombre === name) || null;
};

/**
 * Returns a select value, or null when the value is missing.
 * @param {*} value
 * @returns {*|null}
 */
export const getOptionValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  return value;
};

/**
 * Finds a reference option by id, or null when `options` is not an array.
 * @param {Array|undefined} options
 * @param {*} value
 * @returns {Object|null}
 */
export const getSafeSelectedOption = (options, value) => {
  if (!Array.isArray(options)) {
    logger.warn("Options is not an array:", { data: options });
    return null;
  }
  return getReferencedDataOptionById(options, getOptionValue(value));
};

/**
 * Keeps the first `rangeLimit` words and appends an ellipsis when the text is longer.
 * @param {string} text
 * @param {number} rangeLimit
 * @returns {string}
 */
export const truncateText = (text, rangeLimit) => {
  const words = text.trim().split(/\s+/);
  return words.length > rangeLimit
    ? words.slice(0, rangeLimit).join(" ") + "..."
    : text;
};

/**
 * Turns a stored file URL into the `{ name, size, url }` shape used by file inputs.
 * @param {string|File|null|undefined} url
 * @returns {{name: string, size: number, url: string}|File|null}
 */
export const createFileObjectFromUrl = (url) => {
  if (!url) {
    return null;
  }
  if (url instanceof File) {
    return url;
  }

  const key = extractAssetsObjectKey(url);
  const filename = (key || String(url)).split("/").pop();
  return {
    name: filename,
    size: 0,
    url: url,
  };
};

/** Width of a day or month in `DD/MM/YYYY`. */
const DATE_PART_PAD_LENGTH = 2;

/** Pad character for day and month. */
const DATE_PART_PAD_CHAR = "0";

/**
 * Formats an ISO date as `DD/MM/YYYY`. Returns the input when it cannot be parsed.
 * @param {string|null|undefined} isoDateString
 * @returns {string}
 */
export const formatDate = (isoDateString) => {
  if (!isoDateString) {
    return "";
  }

  try {
    const date = new Date(isoDateString);
    const day = String(date.getDate()).padStart(
      DATE_PART_PAD_LENGTH,
      DATE_PART_PAD_CHAR,
    );
    const month = String(date.getMonth() + 1).padStart(
      DATE_PART_PAD_LENGTH,
      DATE_PART_PAD_CHAR,
    );
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    logger.error("Error formatting date:", { err: sanitizeError(error) });
    return isoDateString;
  }
};

/**
 * Decodes a URI-encoded file name. Returns the original string when decoding fails.
 * @param {string|null|undefined} fileName
 * @returns {string}
 */
export const decodeFileName = (fileName) => {
  if (!fileName) {
    return "";
  }
  try {
    return decodeURIComponent(fileName);
  } catch (e) {
    return fileName;
  }
};

/**
 * Returns the original file name from an S3 key, a historical bucket URL, or an opaque asset ref.
 * @param {string} [src]
 * @returns {string}
 */
export const getAssetFileName = (src = "") => {
  if (!src) {
    return "";
  }
  const key = extractAssetsObjectKey(src);
  const leaf = (key || String(src)).split("/").pop() || "";
  return decodeFileName(leaf.split("?")[0]);
};

const ASSET_DOWNLOAD_CONTAINER_ID = "asset-downloads";
const ASSET_DOWNLOAD_TOAST_ID = "asset-download-error";

const notifyDownloadFailed = () => {
  toast.error(getGenericErrorMessage("FAILED_TO_DOWNLOAD_FILE"), {
    toastId: ASSET_DOWNLOAD_TOAST_ID,
    containerId: ASSET_DOWNLOAD_CONTAINER_ID,
  });
};

const saveBlobAsDownload = (blob, filename) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", filename || "document");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

export const handleFileDownload = async (url, filename = null) => {
  if (!url) {
    return;
  }

  try {
    const downloadSource = await resolveAccessibleAssetUrl(url);
    if (!downloadSource) {
      notifyDownloadFailed();
      return;
    }

    const response = await fetch(downloadSource);
    if (!response.ok) {
      notifyDownloadFailed();
      return;
    }

    const blob = await response.blob();
    const downloadFilename =
      filename || extractAssetsObjectKey(url).split("/").pop() || "document";
    saveBlobAsDownload(blob, downloadFilename);
  } catch (error) {
    logger.error("Error downloading file:", { err: sanitizeError(error) });
    notifyDownloadFailed();
  }
};

/** Upper bound in bytes for a stored object to be considered a URL pointer instead of a real file. */
const MAX_POINTER_SIZE_BYTES = 2048;

/** Maximum pointer hops followed before giving up, so a bad pointer chain cannot loop forever. */
const MAX_POINTER_HOPS = 3;

const POINTER_URL_PATTERN = /^https?:\/\/\S+$/;

/**
 * Some records (e.g. appointment documents) store a small object whose body is the URL of the
 * real file instead of the file itself. Returns that URL when the blob is such a pointer.
 *
 * @param {Blob} blob - Fetched object body.
 * @returns {Promise<string|null>} Target URL, or null when the blob is the actual file.
 */
const readPointerUrl = async (blob) => {
  if (!blob || blob.size === 0 || blob.size > MAX_POINTER_SIZE_BYTES) {
    return null;
  }

  try {
    const text = (await blob.text()).trim();
    return POINTER_URL_PATTERN.test(text) ? text : null;
  } catch (error) {
    return null;
  }
};

/**
 * Downloads a file for FileUploadArea, resolving pointer URLs used by appointment documents.
 * Appointment fields may point at a small S3 object whose body is the real file URL
 * (e.g. under companiesDocuments). Follows those pointers before saving the blob.
 *
 * @param {string} url - File URL (may be a direct file or a pointer object).
 * @param {string|null} [filename=null] - Optional download filename.
 */
export const handleFileUploadAreaDownload = async (url, filename = null) => {
  if (!url) {
    return;
  }

  try {
    let currentUrl = url;
    let blob = null;
    const visitedUrls = new Set();

    for (let hop = 0; hop <= MAX_POINTER_HOPS; hop += 1) {
      if (visitedUrls.has(currentUrl)) {
        break;
      }
      visitedUrls.add(currentUrl);

      const downloadSource = await resolveAccessibleAssetUrl(currentUrl);
      if (!downloadSource) {
        break;
      }

      const response = await fetch(downloadSource);
      if (!response.ok) {
        break;
      }

      blob = await response.blob();

      const pointerUrl = await readPointerUrl(blob);
      if (!pointerUrl) {
        break;
      }

      currentUrl = pointerUrl;
      blob = null;
    }

    if (!blob) {
      logger.error("Could not resolve file URL:", { data: url });
      notifyDownloadFailed();
      return;
    }

    const downloadFilename =
      filename ||
      extractAssetsObjectKey(currentUrl).split("/").pop() ||
      "document";
    saveBlobAsDownload(blob, downloadFilename);
  } catch (error) {
    logger.error("Error downloading file from FileUploadArea:", {
      err: sanitizeError(error),
    });
    notifyDownloadFailed();
  }
};

/**
 * Checks that an RFC matches the 12-character company or 13-character individual pattern.
 * @param {string} rfc
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validateRFC = (rfc) => {
  if (!rfc || typeof rfc !== "string") {
    return { isValid: false, error: "RFC is required" };
  }

  const cleanRFC = rfc.trim().toUpperCase();

  // RFC patterns for different entity types
  const patterns = {
    // Persona Física (13 characters)
    physical: /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/,
    // Persona Moral (12 characters)
    moral: /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/,
  };

  // Check if RFC matches either pattern
  const isValidPhysical = patterns.physical.test(cleanRFC);
  const isValidMoral = patterns.moral.test(cleanRFC);

  if (!isValidPhysical && !isValidMoral) {
    return {
      isValid: false,
      error:
        "Invalid RFC format. Must be 12 characters for companies or 13 characters for individuals",
    };
  }

  // Additional validation for date part (characters 4-9 for physical, 3-8 for moral)
  const dateStart = isValidPhysical
    ? RFC_PHYSICAL_DATE_START
    : RFC_MORAL_DATE_START;
  const dateString = cleanRFC.substring(
    dateStart,
    dateStart + RFC_DATE_LENGTH,
  );
  const month = parseInt(
    dateString.substring(DATE_MONTH_START, DATE_DAY_START),
  );
  const day = parseInt(
    dateString.substring(DATE_DAY_START, DATE_COMPONENT_END),
  );

  // Validate date components
  if (month < MIN_MONTH || month > MAX_MONTH) {
    return { isValid: false, error: "Invalid month in RFC" };
  }
  if (day < MIN_DAY || day > MAX_DAY) {
    return { isValid: false, error: "Invalid day in RFC" };
  }

  return { isValid: true, error: null };
};

/**
 * Checks that an email has a valid shape and stays within length limits.
 * @param {string} email
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== "string") {
    return { isValid: false, error: "Email is required" };
  }

  const cleanEmail = email.trim();

  // RFC 5322 compliant email regex (simplified but comprehensive)
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(cleanEmail)) {
    return { isValid: false, error: "Invalid email format" };
  }

  const EMAIL_MAX_LENGTH = 254;
  const EMAIL_LOCAL_PART_MAX_LENGTH = 64;

  if (cleanEmail.length > EMAIL_MAX_LENGTH) {
    return { isValid: false, error: "Email is too long" };
  }

  const localPart = cleanEmail.split("@")[0];
  if (localPart.length > EMAIL_LOCAL_PART_MAX_LENGTH) {
    return { isValid: false, error: "Email local part is too long" };
  }

  return { isValid: true, error: null };
};

/**
 * Checks a national phone number: 7 to 15 digits, and it cannot start with 0.
 * @param {string} phoneNumber
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return { isValid: false, error: "Phone number is required" };
  }

  const cleanPhone = phoneNumber.trim();

  // Remove all non-digit characters (no + since country code is separate)
  const digitsOnly = cleanPhone.replace(/[^\d]/g, "");

  // National phone number format: 7-15 digits, cannot start with 0
  if (!NATIONAL_PHONE_PATTERN.test(digitsOnly)) {
    return {
      isValid: false,
      error:
        "Invalid phone number format. Must contain 7-15 digits and cannot start with 0",
    };
  }

  // Additional length check for total digits
  if (
    digitsOnly.length < PHONE_MIN_DIGITS ||
    digitsOnly.length > PHONE_MAX_DIGITS
  ) {
    return {
      isValid: false,
      error: "Phone number must contain between 7 and 15 digits",
    };
  }

  return { isValid: true, error: null };
};

export const selectStyles = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: SELECT_BORDER_RADIUS,
    fontSize: SELECT_FONT_SIZE,
    cursor: "pointer",
    borderColor: baseStyles.borderColor,
    marginBottom: SELECT_MARGIN_BOTTOM,
  }),
  container: (baseStyles) => ({
    ...baseStyles,
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
  }),
  input: (baseStyles) => ({
    ...baseStyles,
  }),
  option: (baseStyles) => ({
    ...baseStyles,
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

/**
 * react-select styles with a red border when the field has an error.
 * @param {boolean} hasError
 * @returns {Object}
 */
export const getSelectStylesWithError = (hasError) => ({
  ...selectStyles,
  control: (baseStyles) => ({
    ...selectStyles.control(baseStyles),
    borderColor: hasError
      ? SELECT_ERROR_BORDER_COLOR
      : baseStyles.borderColor,
  }),
});

/**
 * Trims a string, collapses spaces, and removes accent marks for name matching.
 * @param {string} str
 * @returns {string}
 */
export const normalizeString = (str) => {
  return str
    .normalize("NFD") // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, "") // Remove accent marks
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .trim();
};

/**
 * Finds a state whose name matches exactly after accent and space normalization.
 * @param {string} stateName
 * @param {Array<{nombre: string}>} states
 * @returns {Object|undefined}
 */
export const findMatchingState = (stateName, states) => {
  if (!stateName || !states.length) {
    return null;
  }

  const normalizedInput = normalizeString(stateName);

  // Only try exact match
  return states.find(
    (state) => normalizeString(state.nombre) === normalizedInput,
  );
};

/**
 * Finds a town by name, limited to `stateId` when that id is provided.
 * @param {string} townName
 * @param {Array<{nombre: string, idEstado: string}>} towns
 * @param {string|number|null|undefined} stateId
 * @returns {Object|undefined}
 */
export const findMatchingTown = (townName, towns, stateId) => {
  if (!townName || !towns.length) {
    return null;
  }

  const normalizedInput = normalizeString(townName);

  // Filter towns by state if stateId is provided
  const townsInState = stateId
    ? towns.filter((town) => areReferenceIdsEqual(town.idEstado, stateId))
    : towns;

  // Only try exact match
  return townsInState.find(
    (town) => normalizeString(town.nombre) === normalizedInput,
  );
};

/**
 * Finds a company-size option by label, or by the built-in Spanish and English names.
 * @param {string} sizeName
 * @param {Array<{label: string, value: number}>} [companySizeOptions]
 * @returns {Object|null}
 */
export const findMatchingCompanySize = (sizeName, companySizeOptions = []) => {
  if (!sizeName) {
    return null;
  }

  const normalizedInput = normalizeString(sizeName);

  // If companySizeOptions are provided, match against the exact labels from the options
  if (companySizeOptions && companySizeOptions.length > 0) {
    return (
      companySizeOptions.find(
        (option) => normalizeString(option.label) === normalizedInput,
      ) || null
    );
  }

  // Fallback to the original mappings for backward compatibility
  const sizeMappings = [
    { value: 1, names: ["Grande", "grande", "large", "big"] },
    { value: 2, names: ["Mediana", "mediana", "medium", "mediano"] },
    { value: 3, names: ["Micro", "micro"] },
    { value: 4, names: ["Pequeña", "pequeña", "pequeño", "small", "little"] },
    {
      value: 5,
      names: [
        "Indeterminado",
        "indeterminado",
        "indeterminada",
        "undetermined",
        "unknown",
      ],
    },
  ];

  // Only try exact match
  for (const mapping of sizeMappings) {
    if (mapping.names.includes(normalizedInput)) {
      return { value: mapping.value, label: sizeName };
    }
  }

  return null;
};

/**
 * Finds a country-code option, or MX, US, FR, or CA when no option list is passed.
 * @param {string} countryCode
 * @param {Array<{value: string}>} [countryCodeOptions]
 * @returns {Object|null}
 */
export const findMatchingCountryCode = (
  countryCode,
  countryCodeOptions = [],
) => {
  if (!countryCode) {
    return null;
  }

  const normalizedInput = normalizeString(countryCode);

  // If countryCodeOptions are provided, match against the exact values from the options
  if (countryCodeOptions && countryCodeOptions.length > 0) {
    return (
      countryCodeOptions.find(
        (option) => normalizeString(option.value) === normalizedInput,
      ) || null
    );
  }

  // Fallback to the original logic for backward compatibility
  const upperInput = countryCode.toUpperCase().trim();
  const validCountryCodes = ["MX", "US", "FR", "CA"];

  // Check if the input matches any valid country code
  if (validCountryCodes.includes(upperInput)) {
    return { value: upperInput, label: upperInput };
  }

  return null;
};

/**
 * Finds a company user whose `nombre` matches exactly after normalization.
 * @param {string} userName
 * @param {Array<{nombre: string}>} users
 * @returns {Object|undefined|null}
 */
export const findMatchingCustomerCompanyUser = (userName, users) => {
  if (!userName) {
    return null;
  }
  if (!users || !Array.isArray(users) || users.length === 0) {
    return null;
  }

  const normalizedInput = normalizeString(userName);

  // Only try exact match
  return users.find((user) => normalizeString(user.nombre) === normalizedInput);
};

/**
 * Finds a category whose `nombre` matches exactly after normalization.
 * @param {string} categoryName
 * @param {Array<{nombre: string}>} categories
 * @returns {Object|undefined}
 */
export const findMatchingCategory = (categoryName, categories) => {
  if (!categoryName || !categories.length) {
    return null;
  }

  const normalizedInput = normalizeString(categoryName);

  // Only try exact match
  return categories.find(
    (category) => normalizeString(category.nombre) === normalizedInput,
  );
};

/**
 * Finds an active subcategory by name inside one category.
 * @param {string} subcategoryName
 * @param {{subcategorias?: Array}} category
 * @returns {Object|undefined}
 */
export const findMatchingSubcategory = (subcategoryName, category) => {
  if (!subcategoryName || !category || !category.subcategorias) {
    return null;
  }

  const normalizedInput = normalizeString(subcategoryName);

  // Only try exact match with active subcategories only
  return category.subcategorias.find(
    (subcategory) =>
      subcategory.activo &&
      normalizeString(subcategory.nombre) === normalizedInput,
  );
};

/**
 * Maps a status label to `No Requerido`, `Opcional`, or `Requerido`.
 * @param {string} statusName
 * @returns {string|null}
 */
export const findMatchingRequirementStatus = (statusName) => {
  if (!statusName) {
    return null;
  }

  const normalizedInput = normalizeString(statusName);

  // Define the valid status options
  const statusMappings = [
    {
      value: "No Requerido",
      names: ["no requerido", NOT_REQUIRED, "not required", "no required"],
    },
    { value: "Opcional", names: ["opcional", "Opcional", "optional"] },
    {
      value: "Requerido",
      names: ["requerido", "Requerido", "required", "mandatory"],
    },
  ];

  // Only try exact match
  for (const mapping of statusMappings) {
    if (mapping.names.includes(normalizedInput)) {
      return mapping.value;
    }
  }

  // Try exact match with original input (case-insensitive)
  const originalLower = statusName.toLowerCase().trim();
  for (const mapping of statusMappings) {
    for (const name of mapping.names) {
      if (name.toLowerCase() === originalLower) {
        return mapping.value;
      }
    }
  }

  return null;
};

/**
 * Reports whether an event name is already used, ignoring the event being edited.
 * @param {string} eventName
 * @param {Array<{_id: string, nombre: string}>} existingEvents
 * @param {string|null} [currentEventId=null]
 * @returns {{isValid: boolean}}
 */
export const validateBusinessEventName = (
  eventName,
  existingEvents,
  currentEventId = null,
) => {
  if (!eventName || !existingEvents) {
    return {
      isValid: true,
    };
  }

  // Check for exact match (case-insensitive, normalized)
  const duplicateEvent = existingEvents.find((event) => {
    // Skip current event in edit mode
    if (currentEventId && event._id === currentEventId) {
      return false;
    }

    return normalizeString(event.nombre) === normalizeString(eventName);
  });

  if (duplicateEvent) {
    return {
      isValid: false,
    };
  }

  // No duplicate found - name is valid
  return {
    isValid: true,
  };
};
